import { Inject, Injectable, PLATFORM_ID } from '@angular/core';

import { isPlatformBrowser } from '@angular/common';
import { environment } from 'src/environments/environment';
import { CoreModule } from '../core.module';
import { filter } from 'rxjs';
import { Actions, Store, ofActionSuccessful } from '@ngxs/store';
import { UsersState } from '../states/users.state';
import { OrganisationsState } from '../states/organisations.state';
import dayjs from 'dayjs';
import { CreateUser, DeleteUser, SendPhoneNumberVerificationSMS, UpdateUser } from '../states/models/users.state.model';
import { NavigationEnd, Router } from '@angular/router';
import { AssignUploadToProject } from '../states/models/uploads.state.model';
import { AppState } from '../states/app.state';
import type { CLSMetric, INPMetric, LCPMetric } from 'web-vitals';
declare let gtag: Function;

@Injectable({
  providedIn: CoreModule,
})
export class GoogleAnalyticsService {
  constructor(
    private store: Store,
    private actions: Actions,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: object
  ) {}

  public async enableTracking() {
    if (environment.googleAnalytics.enable === false || !isPlatformBrowser(this.platformId)) {
      return;
    }

    this.addGoogleAnalyticsTag();

    gtag('config', 'G-HHXH1S6KF3', { anonymize_ip: true, send_page_view: false });

    this.trackWebVitals();

    // Add consent
    this.store
      .select(AppState.googleAdsConstent)
      .pipe(filter((consentGiven) => consentGiven === true))
      .subscribe(() => {
        gtag('consent', 'update', {
          ad_storage: 'granted',
          ad_personalization: 'granted',
          ad_user_data: 'granted',
        });
      });

    // Track user properties
    this.store
      .select(UsersState.me)
      .pipe(filter((user) => user !== undefined && user !== null))
      .subscribe((user) => {
        this.set('user_id', user.id);
        const organisation = this.store.selectSnapshot(OrganisationsState.myOrganisation);
        this.set('user_properties', {
          roles: user.roles.map((role) => role.name) || [],
          organisation_name: organisation?.name,
          member_since: dayjs(user?.createdAt).format('YYYY-MM'),
        });
      });

    // Track user_features
    this.actions
      .pipe(ofActionSuccessful(CreateUser, UpdateUser, SendPhoneNumberVerificationSMS, DeleteUser))
      .subscribe(() => {
        this.event('manage_user');
      });

    this.actions.pipe(ofActionSuccessful(AssignUploadToProject)).subscribe(() => {
      this.event('assign_upload_to_project');
    });

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.event('page_view', {
        page_title: document.title,
        page_location: window.location.href,
      });
    });
  }

  public set(propertyName: string, value: any) {
    if (environment.googleAnalytics.enable === false) {
      return;
    }
    gtag('set', propertyName, value);
  }
  public event(eventName: string, opts?: { [key: string]: any }) {
    if (environment.googleAnalytics.enable === false) {
      return;
    }
    gtag('event', eventName, {
      content_group: this.urlToContentGroup(window.location.pathname),
      ...opts,
    });
  }

  // Core Web Vitals, sent as in https://github.com/GoogleChrome/web-vitals#send-the-results-to-google-analytics
  // They belong to the landing page, but CLS and INP are only sent when the tab is hidden, often after a few SPA navigations.
  private async trackWebVitals() {
    const pageLocation = window.location.href;
    const contentGroup = this.urlToContentGroup(window.location.pathname);
    const { onCLS, onINP, onLCP } = await import('web-vitals');
    const send = (metric: CLSMetric | INPMetric | LCPMetric) => {
      this.event(metric.name, {
        value: metric.delta, // Delta, so GA4 can sum the reports of one metric_id.
        metric_id: metric.id,
        metric_value: metric.value,
        metric_rating: metric.rating,
        page_location: pageLocation,
        content_group: contentGroup,
      });
    };
    onCLS(send);
    onINP(send);
    onLCP(send);
  }

  private urlToContentGroup(url: string) {
    const contentGroups = [
      { regex: /^\/klic\/(.*)\/viewer$/, value: 'KLIC viewer' },
      { regex: /^\/klic\/(.*)\/detail$/, value: 'KLIC detail' },
      { regex: /^\/klic\/(.*)\/taken$/, value: 'KLIC veilig graven' },
      { regex: /^\/klic\/(.*)\/documenten$/, value: 'KLIC documenten' },
      { regex: /^\/klic\/(.*)\/documenten$/, value: 'KLIC contacten' },
      { regex: /^\/klic\/projects/, value: 'Projecten' },
      { regex: /^\/klic$/, value: 'KLIC overview' },
      { regex: /^\/bestellen/, value: 'Bestellen' },
      { regex: /^\/klic\/upload/, value: 'Upload' },
    ];

    const contentGroup = contentGroups.find((group) => url.match(group.regex) !== null)?.value || 'Niet gespecificeerd';
    return contentGroup;
  }

  private addGoogleAnalyticsTag() {
    const node = document.createElement('script');
    node.src = `https://www.googletagmanager.com/gtag/js?id=${environment.googleAnalytics.tag}`;
    node.async = true;
    document.getElementsByTagName('head')[0].appendChild(node);
  }
}
